import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  cards: null,
  selectedCardId: null,
  campaignBanners: [],
  // Trailing 6-month LEAP cashback keyed by card_id (see fetchBdmcSummaryJson).
  leapRewards: {},
  easySavingsTrx: null,
  fundingPreferences: null,
  hasRecurringPayments: false,
  showRecurringPaymentsTile: false,
  recurringTotal: 0,
  recurringPaymentTrx: null,
  recurringPaymentUsers: null,
  addressStoredInSession: null,
  transientSuccessBanner: null as { type: string; message: string } | null,
  isLoading: false,
  error: null,
}

const bdmcSummarySlice = createSlice({
  name: 'bdmcSummary',
  initialState,
  reducers: {
    fetchStart(state) {
      state.isLoading = true
      state.error = null
    },
    fetchSuccess(state, action) {
      const {
        cards,
        initialCardId,
        campaignBanners,
        leapRewards,
        easySavingsTrx,
        fundingPreferences,
        hasRecurringPayments,
        showRecurringPaymentsTile,
        recurringTotal,
        recurringPaymentTrx,
        recurringPaymentUsers,
        addressStoredInSession,
      } = action.payload
      state.cards = cards
      // Hydrate selectedCardId atomically with cards on first load so
      // selectBdmcCurrentCard returns the URL-specified card immediately —
      // no flash to cards[0] before a useEffect could fire. Preserved on
      // subsequent fetches (retry / refresh) once the user has made a selection.
      if (state.selectedCardId === null) {
        state.selectedCardId = initialCardId ?? null
      }
      state.campaignBanners = campaignBanners || []
      state.leapRewards = leapRewards || {}
      state.easySavingsTrx = easySavingsTrx
      state.fundingPreferences = fundingPreferences
      state.hasRecurringPayments = hasRecurringPayments
      state.showRecurringPaymentsTile = !!showRecurringPaymentsTile
      state.recurringTotal = recurringTotal
      state.recurringPaymentTrx = recurringPaymentTrx
      state.recurringPaymentUsers = recurringPaymentUsers
      state.addressStoredInSession = addressStoredInSession ?? null
      state.isLoading = false
    },
    fetchFailure(state, action) {
      state.error = action.payload
      state.isLoading = false
      // Clear any stale banners so an errored refresh doesn't keep showing them.
      state.campaignBanners = []
    },
    setSelectedCard(state, action) {
      state.selectedCardId = action.payload
    },
    setTransientSuccessBanner(state, action) {
      state.transientSuccessBanner = action.payload
    },
    clearTransientSuccessBanner(state) {
      state.transientSuccessBanner = null
    },
  },
})

export const selectBdmcCards = (state) => state.bdmcSummary.cards
export const selectBdmcSelectedCardId = (state) => state.bdmcSummary.selectedCardId

// The currently-selected card, derived from cards + selectedCardId. Falls back to
// the first card when nothing is selected yet (matching the previous index-0 default).
export const selectBdmcCurrentCard = (state) => {
  const cards = state.bdmcSummary.cards
  if (!cards || cards.length === 0) {
    return null
  }
  const selectedId = state.bdmcSummary.selectedCardId
  return cards.find((card) => card.card_id === selectedId) || cards[0]
}
export const selectBdmcCampaignBanners = (state) => state.bdmcSummary.campaignBanners
export const selectBdmcEasySavings = (state) => state.bdmcSummary.easySavingsTrx
export const selectBdmcFundingPreferences = (state) => state.bdmcSummary.fundingPreferences
export const selectBdmcFundingPreferenceForCard = (state, cardId: string) =>
  state.bdmcSummary.fundingPreferences?.[cardId] ?? {}
export const selectBdmcFundingFlowKindForCard = (state, cardId: string) =>
  state.bdmcSummary.fundingPreferences?.[cardId]?.fundingFlowKind ?? ''
export const selectBdmcIsAutoTopUpActiveForCard = (state, cardId: string) =>
  !!state.bdmcSummary.fundingPreferences?.[cardId]?.isAutoTopUpActive
export const selectBdmcHasRecurringPayments = (state) => state.bdmcSummary.hasRecurringPayments
export const selectBdmcShowRecurringPaymentsTile = (state) =>
  state.bdmcSummary.showRecurringPaymentsTile
export const selectBdmcRecurringTotal = (state) => state.bdmcSummary.recurringTotal
export const selectBdmcRecurringPaymentTrx = (state) => state.bdmcSummary.recurringPaymentTrx
export const selectBdmcRecurringPaymentUsers = (state) => state.bdmcSummary.recurringPaymentUsers

function normalizeTreatmentIds(treatmentIds) {
  if (Array.isArray(treatmentIds)) {
    return treatmentIds
  }

  if (typeof treatmentIds === 'string') {
    return treatmentIds
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
  }

  return []
}

// ELMO treatment ids, sourced from the `experiments` passthrough state.
// Runtime data can arrive as `experiments.treatments` or the legacy
// `experiments.treatmentIds` string from serverData.
export const selectBdmcTreatments = (state) => {
  const experiments = state.experiments || {}

  if (Array.isArray(experiments.treatments) && experiments.treatments.length > 0) {
    return experiments.treatments
  }

  return normalizeTreatmentIds(experiments.treatmentIds)
}

// Trailing 6-month LEAP cashback, keyed by card_id.
export const selectBdmcLeapRewards = (state) => state.bdmcSummary.leapRewards || {}

// 6-month cashback earnings for the currently-selected card, or null.
export const selectBdmcCurrentCardCashback = (state) => {
  const currentCard = selectBdmcCurrentCard(state)
  if (!currentCard) {
    return null
  }
  const leapRewards = state.bdmcSummary.leapRewards || {}
  return leapRewards[currentCard.card_id]?.earnings?.totalCashBackEarnedAmount || null
}

export const selectBdmcIsLoading = (state) => state.bdmcSummary.isLoading
export const selectBdmcError = (state) => state.bdmcSummary.error
export const selectBdmcTransientSuccessBanner = (state) => state.bdmcSummary.transientSuccessBanner

export const {
  fetchStart,
  fetchSuccess,
  fetchFailure,
  setSelectedCard,
  setTransientSuccessBanner,
  clearTransientSuccessBanner,
} = bdmcSummarySlice.actions

export default bdmcSummarySlice.reducer
